"use client";

/**
 * Paywall modal — shown on:
 *  - Tap on any gated feature while not subscribed
 *  - "Manage Billing" tap when no Stripe customer exists
 *  - Expired-trial gated-feature access
 *
 * Implements the acceptance criteria in
 * [[tasks/web-app-billing-management]] "Paywall modal" section.
 */

import { useState } from "react";
import { PRICING_TIERS, type Cadence, type PricingTier } from "@/lib/pricingTiers";
import { redirectToStripeCheckout } from "@/lib/billing";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import {
  markPaywallDismissed,
  wasPaywallRecentlyDismissed,
} from "@/lib/paywallDismiss";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: highlights one tier and pre-selects it for Subscribe CTA. */
  focusTier?: PricingTier["id"];
  /** Override title copy — defaults vary by trigger (expired, gated, etc.). */
  title?: string;
  /** Override body copy above the tier grid. */
  subtitle?: string;
}

export { wasPaywallRecentlyDismissed, markPaywallDismissed };

export function PaywallModal({
  isOpen,
  onClose,
  focusTier,
  title,
  subtitle,
}: PaywallModalProps) {
  const t = useTranslations();
  const resolvedTitle = title ?? t("paywall.defaultTitle");
  const resolvedSubtitle = subtitle ?? t("paywall.defaultSubtitle");
  const [cadence, setCadence] = useState<Cadence>("monthly");
  const [selectedTier, setSelectedTier] = useState<PricingTier["id"]>(
    focusTier ?? PRICING_TIERS.find((t) => t.highlight)?.id ?? "zoey_plus",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { householdId } = useHouseholdContext();

  if (!isOpen) return null;

  async function handleSubscribe() {
    setError(null);
    if (!householdId) {
      setError(t("paywall.errors.noHousehold"));
      return;
    }
    setSubmitting(true);
    try {
      await redirectToStripeCheckout({
        tier: selectedTier,
        cadence,
        householdId,
      });
    } catch (err) {
      console.error("Paywall createCheckoutSession failed", err);
      const code = (err as { code?: string } | null)?.code;
      // Map server gates from createCheckoutSession (PR 1 of the
      // shared-subscription rollout) to user-facing copy:
      //   - permission-denied → CHECKOUT_NON_MEMBER_DENIED or
      //     CHECKOUT_NON_ADULT_DENIED. Either way, the actionable framing
      //     for the caller is the same: ask an adult member.
      //   - failed-precondition → CHECKOUT_ALREADY_COVERED. The
      //     AlreadyCoveredModal in PaywallContext should catch this client-
      //     side first; this is a fail-safe message if the server's view
      //     beats the client's snapshot to the gate.
      if (code === "functions/permission-denied") {
        setError(t("paywall.errors.adultMembersOnly"));
      } else if (code === "functions/failed-precondition") {
        setError(t("paywall.errors.alreadyCovered"));
      } else {
        setError(t("paywall.errors.checkoutFailed"));
      }
      setSubmitting(false);
    }
    // If redirect succeeds, this component unmounts — no need to reset state.
  }

  function handleMaybeLater() {
    markPaywallDismissed();
    onClose();
  }

  const tiers = PRICING_TIERS;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={handleMaybeLater}
        role="presentation"
      />

      <div
        className="relative bg-white dark:bg-gray-900 w-full sm:max-w-3xl sm:mx-4 sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paywall-title"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 px-5 pt-5 pb-3 border-b border-gray-100 dark:border-white/5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="paywall-title"
                className="text-xl font-bold text-gray-900 dark:text-gray-100"
              >
                {resolvedTitle}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {resolvedSubtitle}
              </p>
            </div>
            <button
              onClick={handleMaybeLater}
              aria-label={t("common.close")}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Cadence toggle */}
          <div
            className="mt-4 inline-flex rounded-full bg-gray-100 dark:bg-white/10 p-1"
            role="group"
            aria-label={t("paywall.cadence.aria")}
          >
            <button
              onClick={() => setCadence("monthly")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                cadence === "monthly"
                  ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow"
                  : "text-gray-600 dark:text-gray-400"
              }`}
            >
              {t("paywall.cadence.monthly")}
            </button>
            <button
              onClick={() => setCadence("annual")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                cadence === "annual"
                  ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow"
                  : "text-gray-600 dark:text-gray-400"
              }`}
            >
              {t("paywall.cadence.annual")}
            </button>
          </div>
        </div>

        {/* Tier grid */}
        <div className="px-5 py-5 grid gap-3 sm:grid-cols-3">
          {tiers.map((tier) => {
            const isSelected = selectedTier === tier.id;
            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => setSelectedTier(tier.id)}
                aria-pressed={isSelected}
                className={`relative rounded-2xl border p-4 flex flex-col text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-color,#00C7BE)] ${
                  isSelected
                    ? "border-[color:var(--accent-color,#00C7BE)] ring-1 ring-[color:var(--accent-color,#00C7BE)] bg-[color:var(--accent-color,#00C7BE)]/5"
                    : "border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20"
                }`}
              >
                {tier.badge && (
                  <span
                    className="absolute -top-2 right-3 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
                  >
                    {tier.badge}
                  </span>
                )}
                <h3 className="text-sm font-bold">{tier.name}</h3>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-xl font-bold">
                    {cadence === "monthly" ? tier.monthlyPrice : tier.annualPrice}
                  </span>
                  <span className="text-xs text-gray-500">
                    {cadence === "monthly" ? t("paywall.perMonth") : t("paywall.perYear")}
                  </span>
                </div>
                {cadence === "annual" && (
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">
                    {tier.annualHelper}
                  </span>
                )}
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 leading-snug">
                  {tier.description}
                </p>
                <ul className="mt-3 space-y-1.5 text-xs text-gray-700 dark:text-gray-300 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <span
                        className="mt-0.5 shrink-0 text-[color:var(--accent-color,#00C7BE)]"
                        aria-hidden="true"
                      >
                        {"✓"}
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div
            className="mx-5 mb-3 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 px-5 py-4 border-t border-gray-100 dark:border-white/5 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={handleMaybeLater}
            disabled={submitting}
            className="order-2 sm:order-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
          >
            {t("paywall.maybeLater")}
          </button>
          <button
            onClick={handleSubscribe}
            disabled={submitting}
            className="order-1 sm:order-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-60"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {submitting ? t("paywall.openingCheckout") : t("paywall.subscribe")}
          </button>
        </div>
      </div>
    </div>
  );
}
